/* eslint-disable react/prop-types, no-unused-vars */
import { useState } from 'react';
import { Modal, Button, Table, Segmented, Alert, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

const PreviewModal = ({
    visible,
    onClose,
    steps,
    dryRunResult,
    loading,
}) => {
    const [viewMode, setViewMode] = useState('Side-by-Side');

    const enabledSteps = steps.filter(s => s.enabled !== false);

    const toTableColumns = (columnNames) => (columnNames || []).map(key => ({
        title: key,
        dataIndex: key,
        key,
        ellipsis: true,
        width: 150,
    }));

    // Use explicit column arrays from backend when available, fall back to Object.keys
    const originalColumns = dryRunResult?.originalColumns
        ? toTableColumns(dryRunResult.originalColumns)
        : toTableColumns(Object.keys(dryRunResult?.originalData?.[0] || {}));

    const transformedColumns = dryRunResult?.transformedColumns
        ? toTableColumns(dryRunResult.transformedColumns)
        : toTableColumns(Object.keys(dryRunResult?.transformedData?.[0] || {}));

    const previewDetails = dryRunResult ? [
        dryRunResult.previewMode ? `Mode: ${dryRunResult.previewMode}` : null,
        typeof dryRunResult.sourceRowsScanned === 'number' ? `Source rows scanned: ${dryRunResult.sourceRowsScanned}` : null,
        typeof dryRunResult.outputRowsReturned === 'number' ? `Rows shown: ${dryRunResult.outputRowsReturned}` : null,
        typeof dryRunResult.outputRowsTotal === 'number' ? `Output rows total: ${dryRunResult.outputRowsTotal}` : null,
    ].filter(Boolean).join(' | ') : '';

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            width="90vw"
            style={{ top: 20 }}
            title={null}
            closable={false}
            destroyOnHidden
        >
            <div className="flex flex-col gap-4" data-testid="pipeline-preview-modal">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="m-0 text-lg font-semibold">Transformation Preview</h3>
                        <p className="m-0 text-sm text-gray-500">
                            {enabledSteps.length} active transformations applied
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Segmented
                            options={['Before', 'Side-by-Side', 'After']}
                            value={viewMode}
                            onChange={setViewMode}
                        />
                        <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Spin size="large" tip="Generating preview..." />
                    </div>
                ) : dryRunResult ? (
                    <>
                    {previewDetails && (
                        <Alert
                            type={dryRunResult.isSampled ? 'warning' : 'info'}
                            showIcon
                            message={dryRunResult.isSampled ? 'Sample preview' : 'Aggregate preview'}
                            description={previewDetails}
                        />
                    )}
                    <div className="flex gap-4">
                        {(viewMode === 'Before' || viewMode === 'Side-by-Side') && (
                            <div className={viewMode === 'Side-by-Side' ? 'w-1/2' : 'w-full'}>
                                <h4 className="mb-2 text-sm font-medium">
                                    Original Data ({dryRunResult.originalData?.length || 0} rows)
                                </h4>
                                <Table
                                    size="small"
                                    dataSource={dryRunResult.originalData || []}
                                    columns={originalColumns}
                                    pagination={false}
                                    scroll={{ x: 'max-content', y: 300 }}
                                    rowKey={(_, i) => i}
                                />
                            </div>
                        )}
                        {(viewMode === 'After' || viewMode === 'Side-by-Side') && (
                            <div className={viewMode === 'Side-by-Side' ? 'w-1/2' : 'w-full'}>
                                <h4 className="mb-2 text-sm font-medium">
                                    Transformed Data ({dryRunResult.transformedData?.length || 0} rows)
                                </h4>
                                <Table
                                    size="small"
                                    dataSource={dryRunResult.transformedData || []}
                                    columns={transformedColumns}
                                    pagination={false}
                                    scroll={{ x: 'max-content', y: 300 }}
                                    rowKey={(_, i) => i}
                                />
                            </div>
                        )}
                    </div>
                    </>
                ) : (
                    <div className="py-16 text-center text-gray-400">
                        <p>No preview data available. The preview will appear once the dry-run completes.</p>
                    </div>
                )}

                {/* Applied Transformations */}
                <div>
                    <h4 className="mb-2 text-sm font-medium">Applied Transformations</h4>
                    <ol className="m-0 list-decimal pl-5 text-sm text-gray-600">
                        {enabledSteps.map((step, i) => (
                            <li key={i}>
                                <strong>{step.label || step.type}</strong>
                                {' — '}
                                <code className="text-xs">{JSON.stringify(step.config)}</code>
                            </li>
                        ))}
                    </ol>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                    <Alert
                        type="info"
                        showIcon
                        title="This is a preview. The underlying dataset will not be modified."
                        className="flex-1"
                    />
                    <div className="ml-4">
                        <Button onClick={onClose} data-testid="pipeline-preview-close-button">Close Preview</Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default PreviewModal;
